import { ReactNode, useEffect, useMemo, useState } from "react";
import {
  MotifModal,
  MotifModalHeader,
  MotifModalBody,
  MotifModalFooter,
  MotifButton,
  MotifTextArea,
  MotifFormField,
  MotifCheckbox,
  MotifProgressLoader,
  MotifTooltip,
  MotifIcon,
  MotifTruncate,
} from "@ey-xd/motif-react";
import {
  IconoirHelpCircle,
  IconoirInfoCircle,
  IconoirXmark,
} from "@ey-xd/motif-icon";
import "./ReturnOrderModal.scss";
import { formatCurrency, getFormattedDate } from "../../helper";
import { SkuTransaction, ReturnOrderModalProps } from "../../types";
import { fetchOrderPipelineListData } from "../../../../store/slices/OrderPipelineSlice";
import { AppDispatch } from "../../../../store";
import { useDispatch } from "react-redux";
import {
  handleNumber,
  formatNumberWithZeroPrefix,
} from "../../../../utils/common";
import { isStringHavingSpecialChars } from "../../../../utils/Validator";
import { fixedCostInfo, variableCostInfo } from "../../../../utils/skuTable";
import { RETURN_REASON_MAX_LENGTH } from "../../../../Constant";
import NotificationGrid from "../../../Notification/NotificationGrid";
import PreviousReturn from "./Component/PreviousReturn";

type ReturnTab = "request" | "previous";

interface SkuTransactionRow extends SkuTransaction {
  alreadyReturnedUnits: number;
  alreadyRequestedUnits: number;
  isPartiallyApproved: boolean;
  pendingApproval: boolean;
  availableUnits: number;
}

interface ReturnSkuApiTransaction {
  CostIntervalCd?: string;
  CostMetric?: string;
  CostMetricCd?: string;
  CostMetricCost?: number | string;
  Description?: string;
  EngagementId?: string;
  IntervalEndDtm?: string;
  IntervalNum?: number | string;
  IntervalStartDtm?: string;
  Intervals?: number | string;
  IsReturnable: number | string;
  Name?: string;
  OrderParentId?: number | string;
  Quantity?: number | string;
  SkuId?: string;
  SkuName?: string;
  StatusCd?: string;
  TotalCost?: number | string;
  TransactionDtm?: string;
  TransactionId: number | string;
  UnitCost?: number | string;
  UnitUsed?: number | string;
  ReturnableUnit?: number | string | null;
  ReturnUnitStatus?: string | null;
  ReturnTransactionStatus?: string | null;
}

interface OrderApiActionResult<T> {
  payload?: {
    Code?: number;
    Message?: string;
    message?: string;
    Data?: {
      Result?: T[];
    };
  };
}

const INVALID_INPUT_ERROR_MESSAGE =
  "Invalid input detected. Please review the input text and try again!";

export default function ReturnOrderModal({
  setShow,
  skuDetails,
  show,
  handleOrderReturnSuccess,
}: ReturnOrderModalProps) {
  const dispatch = useDispatch<AppDispatch>();

  const [returnReason, setReturnReason] = useState("");
  const [isSubmitApiLoading, setIsSubmitLoading] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [skuTransactionList, setSkuTransactionList] = useState<
    SkuTransactionRow[]
  >([]);
  const [activeTab, setActiveTab] = useState<ReturnTab>("request");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [returnReasonApiError, setReturnReasonApiError] = useState("");

  const metricUnitMapping: Record<string, string> = {
    gb: "GB",
    hrs: "Hour",
    cost: "Instance",
    token: "Token",
    license: "License",
    unit: "Unit",
  };

  const getUnitsUsed = (
    costMetric: string,
    quantity: number | undefined | null | "",
  ) => {
    if (quantity === null || quantity === undefined || quantity === "") {
      return "";
    }
    const metricUnit = costMetric?.split("_")?.[1]?.trim();
    return metricUnit && Object.keys(metricUnitMapping).includes(metricUnit)
      ? metricUnitMapping[metricUnit] + (quantity && quantity > 1 ? "s" : "")
      : metricUnit;
  };

  const getCostInterval = (costMetric: string) => {
    const metricUnit = costMetric?.split("_")?.[1]?.trim();
    return `Per ${metricUnitMapping[metricUnit]}`;
  };

  useEffect(() => {
    if (show) {
      loadReturnModalData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    show,
    skuDetails?.OrderSkuId,
    skuDetails?.SkuId,
    skuDetails?.OrderParentId,
  ]);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage("");
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  const isTransactionSelectable = (transaction: SkuTransactionRow) =>
    transaction.IsReturnable === 1 &&
    !transaction.pendingApproval &&
    transaction.availableUnits > 0;

  const buildTransactionRows = (
    transactions: ReturnSkuApiTransaction[] = [],
  ) => {
    const toNumber = (value: unknown, fallback = 0) => {
      const parsedValue = Number(value);
      return Number.isNaN(parsedValue) ? fallback : parsedValue;
    };

    return transactions
      .map((transaction: ReturnSkuApiTransaction) => {
        const transactionId = toNumber(transaction.TransactionId);
        const quantity = toNumber(transaction.Quantity ?? transaction.UnitUsed);
        const unitCost = toNumber(
          transaction.CostMetricCost ?? transaction.UnitCost,
        );
        const directReturnableUnitsRaw = transaction.ReturnableUnit;
        const hasDirectReturnableUnits =
          directReturnableUnitsRaw !== undefined &&
          directReturnableUnitsRaw !== null &&
          directReturnableUnitsRaw !== "";
        const directReturnableUnits = hasDirectReturnableUnits
          ? Math.max(toNumber(directReturnableUnitsRaw), 0)
          : null;
        const availableUnits = directReturnableUnits ?? Math.max(quantity, 0);
        const alreadyRequestedUnits = Math.max(quantity - availableUnits, 0);
        const returnUnitStatus = String(transaction.ReturnUnitStatus ?? "")
          .toLowerCase()
          .trim();
        const isPartiallyApproved =
          returnUnitStatus.includes("partially approved");
        const returnTransactionStatus = String(
          transaction.ReturnTransactionStatus ?? "",
        )
          .toLowerCase()
          .trim();
        const pendingApproval =
          returnUnitStatus.includes("approval") ||
          returnTransactionStatus.includes("submitted") ||
          returnTransactionStatus.includes("approval") ||
          returnTransactionStatus.includes("pending") ||
          false;

        const normalizedTransaction: SkuTransactionRow = {
          CostIntervalCd: String(transaction.CostIntervalCd ?? ""),
          CostMetricCd: String(transaction.CostMetricCd ?? ""),
          CostMetric: String(transaction.CostMetric ?? ""),
          CostMetricCost: unitCost,
          Description: String(transaction.Description ?? ""),
          EngagementId: String(transaction.EngagementId ?? ""),
          IntervalEndDtm: String(transaction.IntervalEndDtm ?? ""),
          IntervalNum: toNumber(transaction.IntervalNum),
          IntervalStartDtm: String(transaction.IntervalStartDtm ?? ""),
          Intervals: toNumber(transaction.Intervals),
          IsReturnable: toNumber(transaction.IsReturnable),
          Name: String(transaction.Name ?? ""),
          OrderParentId: toNumber(transaction.OrderParentId),
          Quantity: quantity,
          SkuId: String(transaction.SkuId ?? ""),
          SkuName: String(transaction.SkuName ?? ""),
          StatusCd: String(transaction.StatusCd ?? ""),
          TotalCost: toNumber(transaction.TotalCost),
          TransactionDtm: String(transaction.TransactionDtm ?? ""),
          TransactionId: transactionId,
          isValidReturnQuantity: true,
          isSelected: false,
          returnAmount: 0,
          returnUnit: "",
          alreadyReturnedUnits: alreadyRequestedUnits,
          alreadyRequestedUnits,
          isPartiallyApproved,
          pendingApproval,
          availableUnits,
        };

        return normalizedTransaction;
      })
      .filter((transaction: SkuTransactionRow) => {
        if (transaction.availableUnits <= 0) {
          return false;
        }

        return (
          Number(transaction.IsReturnable) === 1 || transaction.pendingApproval
        );
      });
  };

  const extractResult = <T,>(action: unknown): T[] => {
    const result = (action as OrderApiActionResult<T>)?.payload?.Data?.Result;
    return Array.isArray(result) ? result : [];
  };

  const loadReturnModalData = () => {
    setIsLoading(true);
    setSelectAll(false);

    dispatch(
      fetchOrderPipelineListData({
        Action: "retriveAllTransactionReturnSku",
        OrderSkuId: skuDetails?.OrderSkuId,
      }),
    )
      .then((transactionAction: unknown) => {
        const transactions =
          extractResult<ReturnSkuApiTransaction>(transactionAction);
        setSkuTransactionList(buildTransactionRows(transactions));
      })
      .catch(() => {
        setSkuTransactionList([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const closeModal = () => {
    setIsSubmitLoading(false);
    setReturnReason("");
    setReturnReasonApiError("");
    setSelectAll(false);
    setActiveTab("request");
    setShow(false);
    setSkuTransactionList([]);
  };

  const handleSelectChange = (transactionId: number) => {
    const updatedList = skuTransactionList.map((transaction) => {
      if (transaction.TransactionId === transactionId) {
        if (!isTransactionSelectable(transaction)) {
          return transaction;
        }
        return {
          ...transaction,
          isSelected: !transaction.isSelected,
          returnUnit: transaction.isSelected ? "" : transaction.returnUnit,
          returnAmount: transaction.isSelected ? 0 : transaction.returnAmount,
        };
      }
      return transaction;
    });
    const allSelected = updatedList
      .filter((t) => isTransactionSelectable(t))
      .every((t) => t.isSelected);
    setSelectAll(allSelected);
    setSkuTransactionList(updatedList);
  };

  const handleSelectAll = () => {
    if (skuTransactionList.length) {
      const updatedList = skuTransactionList.map((transaction) => {
        if (isTransactionSelectable(transaction)) {
          return { ...transaction, isSelected: !selectAll };
        }
        return transaction;
      });
      setSkuTransactionList(updatedList);
      setSelectAll(!selectAll);
    }
  };

  const isSelectAllDisabled = useMemo(() => {
    return skuTransactionList.every((t) => !isTransactionSelectable(t));
  }, [skuTransactionList]);

  const handleUnitChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    transactionId: number,
  ) => {
    const value = event.target.value;
    const updatedList = skuTransactionList.map((transaction) => {
      if (transaction.TransactionId === transactionId) {
        if (!isTransactionSelectable(transaction)) {
          return transaction;
        }
        const val = handleNumber(value);
        const isTrailingDecimal = val.endsWith(".");
        const amount = Number(val);
        const isAmountWithinAllowedRange =
          !Number.isNaN(amount) &&
          amount > 0 &&
          amount <= Number(transaction.availableUnits);
        const canComputeReturnAmount =
          isAmountWithinAllowedRange && !isTrailingDecimal;
        const isValidReturnQuantity =
          val === "" || isTrailingDecimal || isAmountWithinAllowedRange;
        const shouldSelect = val.trim() !== "";

        return {
          ...transaction,
          isSelected: shouldSelect,
          returnAmount: canComputeReturnAmount
            ? amount * transaction.CostMetricCost
            : 0,
          returnUnit: val,
          isValidReturnQuantity,
        };
      }
      return transaction;
    });
    const allSelected = updatedList
      .filter((t) => isTransactionSelectable(t))
      .every((t) => t.isSelected);
    setSelectAll(allSelected);
    setSkuTransactionList(updatedList);
  };

  const handleBlur = (
    event: React.FocusEvent<HTMLInputElement>,
    transactionId: number,
  ) => {
    const { value } = event.target;
    const updatedList = skuTransactionList.map((transaction) => {
      if (transaction.TransactionId === transactionId) {
        if (!isTransactionSelectable(transaction)) {
          return transaction;
        }
        let val = formatNumberWithZeroPrefix(value);
        if (val.endsWith(".") && Number(val) > 0) {
          val = `${val}0`;
        }
        const amount = Number(val);
        const isAmountValid =
          !Number.isNaN(amount) &&
          amount > 0 &&
          amount <= Number(transaction.availableUnits);
        const shouldSelect = val.trim() !== "";

        return {
          ...transaction,
          isSelected: shouldSelect,
          returnAmount: isAmountValid ? amount * transaction.CostMetricCost : 0,
          returnUnit: val,
          isValidReturnQuantity: isAmountValid || val === "",
        };
      }
      return transaction;
    });
    const allSelected = updatedList
      .filter((t) => isTransactionSelectable(t))
      .every((t) => t.isSelected);
    setSelectAll(allSelected);
    setSkuTransactionList(updatedList);
  };

  const handleClearUnit = (transactionId: number) => {
    const updatedList = skuTransactionList.map((transaction) => {
      if (transaction.TransactionId !== transactionId) {
        return transaction;
      }
      if (!isTransactionSelectable(transaction)) {
        return transaction;
      }

      return {
        ...transaction,
        isSelected: false,
        returnUnit: "",
        returnAmount: 0,
        isValidReturnQuantity: true,
      };
    });

    const allSelected = updatedList
      .filter((t) => isTransactionSelectable(t))
      .every((t) => t.isSelected);
    setSelectAll(allSelected);
    setSkuTransactionList(updatedList);
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (isStringHavingSpecialChars(e.target.value)) {
      return;
    }
    setReturnReasonApiError("");
    setReturnReason(e.target.value);
  };

  const submitHandler = () => {
    setIsSubmitLoading(true);
    setErrorMessage("");
    setReturnReasonApiError("");
    dispatch(
      fetchOrderPipelineListData({
        Action: "submitordertransaction",
        ReturnReason: returnReason,
        Transactions: skuTransactionList
          .filter((transaction) => transaction.isSelected)
          .map((transaction) => ({
            OrderSkuId: skuDetails?.OrderSkuId,
            Quantity: Number(transaction.returnUnit),
            TotalCost: Number(transaction.returnAmount.toFixed(2)),
            TransactionId: transaction.TransactionId,
          })),
      }),
    )
      .then((action: unknown) => {
        const response = (action as OrderApiActionResult<unknown>)?.payload;
        const responseMessage = response?.Message || response?.message || "";
        setIsLoading(false);
        if (response?.Code === 200) {
          closeModal();
          handleOrderReturnSuccess();
        } else {
          if (responseMessage === INVALID_INPUT_ERROR_MESSAGE) {
            setReturnReasonApiError(INVALID_INPUT_ERROR_MESSAGE);
          } else {
            setErrorMessage(
              responseMessage ||
                "An error occurred while submitting the return request. Please try again.",
            );
          }
          setIsSubmitLoading(false);
        }
      })
      .catch(() => {
        setIsLoading(false);
        setErrorMessage(
          "An error occurred while submitting the return request. Please try again.",
        );
        setIsSubmitLoading(false);
      });
  };

  const isEnabled = useMemo(() => {
    const selectedRows = skuTransactionList.filter(
      (transaction) => transaction.isSelected,
    );
    if (!selectedRows.length) return false;
    return (
      selectedRows.every((transaction) => {
        return (
          Number(transaction.returnUnit) <=
          Number(transaction.availableUnits) &&
          Number(transaction.returnUnit) > 0 &&
          transaction.isValidReturnQuantity
        );
      }) && returnReason.trim() !== ""
    );
  }, [skuTransactionList, returnReason]);

  const getInputValidationMessage = (transaction: SkuTransactionRow) => {
    if (transaction.returnUnit === "" || transaction.isValidReturnQuantity) {
      return "";
    }

    const numericValue = Number(transaction.returnUnit);
    if (Number.isNaN(numericValue) || numericValue <= 0) {
      return "Value should be greater than 0";
    }
    if (numericValue > Number(transaction.availableUnits)) {
      return `Can’t exceed ${transaction.availableUnits}`;
    }

    return "Enter a valid return unit";
  };

  const formatUnitValue = (value: number) => {
    if (Number.isNaN(value)) {
      return "0";
    }
    return Number.isInteger(value)
      ? String(value)
      : value.toFixed(2).replace(/\.?0+$/, "");
  };

  const pricingType = useMemo(() => {
    const costMetricCode = String(skuDetails?.CostMetricCd || "").toLowerCase();
    if (costMetricCode.startsWith("var")) {
      return "Variable";
    }
    if (costMetricCode.startsWith("fix")) {
      return "Fixed";
    }
    return "";
  }, [skuDetails?.CostMetricCd]);

  const pricingTypeTooltipContent = useMemo(() => {
    if (pricingType === "Fixed") {
      return fixedCostInfo();
    }
    if (pricingType === "Variable") {
      return variableCostInfo();
    }
    return null;
  }, [pricingType]);

  const renderHeaderWithTooltip = (
    label: string,
    tooltipContent: ReactNode,
  ) => {
    return (
      <div className="header-with-tooltip">
        <span>{label}</span>
        <MotifTooltip
          contentClassName="tooltip-content return-modal-tooltip-content"
          allowHover
          hideArrow
          trigger={
            <span className="tooltip-icon-wrapper">
              <MotifIcon
                iconFunction={IconoirHelpCircle}
                size="16"
                fill="#AFAEBA"
              />
            </span>
          }
        >
          <>
            <div className="info-title">{label}</div>
            <div className="info-content">{tooltipContent}</div>
          </>
        </MotifTooltip>
      </div>
    );
  };

  const renderUsagePeriodInTwoLines = (startDate: string, endDate: string) => {
    return (
      <div className="usage-period-wrapper">
        <div className="date-line">{`${startDate} -`}</div>
        <div className="date-line">{endDate}</div>
      </div>
    );
  };

  const renderTopInfoValue = (value: string | number | undefined | null) => {
    const displayValue = String(value ?? "").trim() || "--";
    return (
      <MotifTruncate
        maxLines={1}
        maxWidth={180}
        className="value top-info-truncate"
        tooltipProps={{
          placement: "top",
        }}
      >
        {displayValue}
      </MotifTruncate>
    );
  };

  return (
    <MotifModal
      onClose={isSubmitApiLoading ? undefined : closeModal}
      show={show}
      size="lg"
      className="return-order-modal"
    >
      <MotifModalHeader>
        <span className="header-name">Return submission</span>
        {errorMessage && (
          <div className="error-message-div">
            <NotificationGrid show={true} type="error" message={errorMessage} />
          </div>
        )}
      </MotifModalHeader>
      <MotifModalBody>
        <div className="motif-row sku-detail-row">
          <div className="motif-col-lg-3 top-info-col">
            <div className="label">SKU</div>
            {renderTopInfoValue(skuDetails?.Name)}
            <div className="value-id">
              {skuDetails?.SkuId}
              {pricingType ? (
                <MotifTooltip
                  contentClassName="tooltip-content return-modal-tooltip-content"
                  placement="top"
                  hideCloseButton
                  flip
                  offset={[0, 8]}
                  allowHover
                  allowClick={true}
                  trigger={
                    <span className="pricing-type-badge">{pricingType}</span>
                  }
                  variant="default"
                >
                  <div className="label-info-icon-text">
                    {pricingTypeTooltipContent}
                  </div>
                </MotifTooltip>
              ) : null}
            </div>
          </div>
          <div className="motif-col-lg-3 top-info-col">
            <div className="label">PRODUCT</div>
            {renderTopInfoValue(skuDetails?.ProductName)}
            <div className="value-id">{skuDetails?.ProductId || "--"}</div>
          </div>
          <div className="motif-col-lg-3 top-info-col">
            <div className="label">ORDER</div>
            {renderTopInfoValue(skuDetails?.Description)}
            <div className="value-id">{skuDetails?.OrderParentId || "--"}</div>
          </div>
          <div className="motif-col-lg-3 top-info-col">
            <div className="label">ENGAGEMENT</div>
            {renderTopInfoValue(
              (skuDetails as Partial<{ EngagementName: string }>)
                ?.EngagementName,
            )}
            <div className="value-id">{skuDetails?.EngagementId || "--"}</div>
          </div>
        </div>
        <div className="return-flow-tabs">
          <button
            type="button"
            className={activeTab === "request" ? "tab-btn active" : "tab-btn"}
            onClick={() => setActiveTab("request")}
          >
            Request a return
          </button>
          <button
            type="button"
            className={activeTab === "previous" ? "tab-btn active" : "tab-btn"}
            onClick={() => setActiveTab("previous")}
          >
            Previous Returns
          </button>
        </div>
        {activeTab === "request" ? (
          isLoading ? (
            <div className="table-loader">
              <MotifProgressLoader
                aria-busy="true"
                aria-label="This page is loading"
                aria-live="polite"
                arialabel="This page is loading"
                show
                variant="default"
                className="home-orderpipeline-progress"
              />
            </div>
          ) : (
            <>
              <div className="table-container request-table-container">
                <table className="motif-table-standard request-table">
                  <thead>
                    <tr>
                      <th className="checkbox-column">
                        <MotifCheckbox
                          id="select-all-checkbox"
                          name="select-all-checkbox"
                          className="transaction-select-checkbox"
                          checked={selectAll}
                          onChange={handleSelectAll}
                          disabled={isSelectAllDisabled}
                        >
                          {""}
                        </MotifCheckbox>
                      </th>
                      <th className="usage-period-column">
                        {renderHeaderWithTooltip(
                          "Usage period",
                          "The start and end dates during which the SKU is used.",
                        )}
                      </th>
                      <th className="transaction-date-column">
                        {renderHeaderWithTooltip(
                          "Transaction date",
                          "The date the transaction was sent to the financial system and charged to the engagement code.",
                        )}
                      </th>
                      <th className="usage-interval-column">
                        {renderHeaderWithTooltip(
                          "Usage interval",
                          "Shows two values: the number of consecutive transactions and the total number of requested periods.",
                        )}
                      </th>
                      <th className="text-right">
                        {renderHeaderWithTooltip(
                          "Unit cost",
                          <>
                            Price you will pay for each unit you are ordering.
                            These are the unit type of products:
                            <ul>
                              <li>GBs</li>
                              <li>Hours</li>
                              <li>Licences</li>
                              <li>Instance</li>
                              <li>Tokens</li>
                              <li>Units</li>
                              <li>
                                One time - example would be setup fee to enable
                                a product ready for use.
                              </li>
                            </ul>
                          </>,
                        )}
                      </th>
                      <th>
                        {renderHeaderWithTooltip(
                          "Units used",
                          "The number of units and cost consumed during the usage period. For variable SKUs, the ordered units may differ from the consumed units.",
                        )}
                      </th>
                      <th>
                        {renderHeaderWithTooltip(
                          "Returnable units",
                          "The number of units and cost available to request as a return.",
                        )}
                      </th>
                      <th className="return-unit-column">
                        {renderHeaderWithTooltip(
                          "Return unit",
                          "The number of units that will be requested in the return",
                        )}
                      </th>
                      <th className="text-right return-cost-column">
                        {renderHeaderWithTooltip(
                          "Return cost",
                          "The total cost of returned units, calculated as: Return cost = Unit cost × Returnable units",
                        )}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {skuTransactionList.length > 0 &&
                      skuTransactionList.map((transaction) => {
                        const inputValidationMessage =
                          getInputValidationMessage(transaction);
                        const shouldShowReturnCost =
                          transaction.returnUnit !== "" &&
                          transaction.isValidReturnQuantity &&
                          Number(transaction.returnAmount) > 0;
                        const requestedOrReturnedUnits = Math.max(
                          transaction.alreadyRequestedUnits,
                          transaction.alreadyReturnedUnits,
                        );
                        const returnableTooltipText =
                          requestedOrReturnedUnits > 0
                            ? `A ${formatUnitValue(requestedOrReturnedUnits)} ${transaction.CostMetric} have been already requested. Select the Previous return tab, to view details.`
                            : "";

                        return (
                          <tr
                            key={transaction.TransactionId}
                            className={
                              transaction.isSelected
                                ? "row-selected"
                                : transaction.pendingApproval
                                  ? "row-locked"
                                  : ""
                            }
                          >
                            <td className="checkbox-cell">
                              <div className="checkbox-column">
                                <MotifCheckbox
                                  id={`select-${transaction.TransactionId}`}
                                  name={`select-${transaction.TransactionId}`}
                                  onChange={() =>
                                    handleSelectChange(
                                      transaction.TransactionId,
                                    )
                                  }
                                  checked={transaction.isSelected}
                                  className="transaction-select-checkbox"
                                  disabled={
                                    !isTransactionSelectable(transaction)
                                  }
                                >
                                  {""}
                                </MotifCheckbox>
                              </div>
                            </td>
                            <td className="usage-period-cell">
                              {renderUsagePeriodInTwoLines(
                                getFormattedDate(transaction.IntervalStartDtm),
                                getFormattedDate(transaction.IntervalEndDtm),
                              )}
                            </td>
                            <td className="transaction-date-cell">
                              <div className="transaction-date-value">
                                {getFormattedDate(transaction.TransactionDtm)}
                              </div>
                            </td>
                            <td className="usage-interval-cell">{`${transaction.IntervalNum} of ${transaction.Intervals}`}</td>
                            <td className="text-right">
                              <div>
                                {formatCurrency(transaction.CostMetricCost)}
                              </div>
                              <div className="secondary-value">
                                {getCostInterval(transaction.CostMetricCd)}
                              </div>
                            </td>
                            <td>
                              <div>{`${transaction.Quantity} ${getUnitsUsed(transaction.CostMetricCd, transaction.Quantity)}`}</div>
                              <div className="secondary-value">
                                {formatCurrency(transaction.TotalCost)}
                              </div>
                            </td>
                            <td>
                              <div className="value-with-tooltip">
                                <span>{`${transaction.availableUnits} ${getUnitsUsed(transaction.CostMetricCd, transaction.availableUnits)}`}</span>
                                {returnableTooltipText && (
                                  <MotifTooltip
                                    contentClassName="tooltip-content return-modal-tooltip-content"
                                    allowHover
                                    hideArrow
                                    trigger={
                                      <span className="tooltip-icon-wrapper">
                                        <MotifIcon
                                          iconFunction={IconoirInfoCircle}
                                          size="16"
                                          fill="#AFAEBA"
                                        />
                                      </span>
                                    }
                                  >
                                    <>
                                      <div className="info-title">
                                        Previous partial return
                                      </div>
                                      <div className="info-content">
                                        {returnableTooltipText}
                                      </div>
                                    </>
                                  </MotifTooltip>
                                )}
                              </div>
                            </td>
                            <td className="return-unit-cell">
                              {isTransactionSelectable(transaction) ? (
                                <>
                                  <div className="quantity-input-wrapper">
                                    <input
                                      type="text"
                                      onChange={(e) =>
                                        handleUnitChange(
                                          e,
                                          transaction.TransactionId,
                                        )
                                      }
                                      value={transaction.returnUnit}
                                      onBlur={(e) =>
                                        handleBlur(e, transaction.TransactionId)
                                      }
                                      className={`quantity-input ${inputValidationMessage ? "input-error" : ""}`}
                                      placeholder="Enter value"
                                    />
                                    {transaction.returnUnit !== "" && (
                                      <button
                                        type="button"
                                        className="quantity-clear-btn"
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() =>
                                          handleClearUnit(
                                            transaction.TransactionId,
                                          )
                                        }
                                        aria-label="Clear return unit value"
                                      >
                                        <MotifIcon
                                          iconFunction={IconoirXmark}
                                          size="14"
                                        />
                                      </button>
                                    )}
                                  </div>
                                  {inputValidationMessage && (
                                    <div className="error-message">
                                      {inputValidationMessage}
                                    </div>
                                  )}
                                </>
                              ) : transaction.pendingApproval ? (
                                <div className="value-with-tooltip pending-approval-with-tooltip">
                                  <span>In approval</span>
                                  <MotifTooltip
                                    contentClassName="tooltip-content return-modal-tooltip-content"
                                    allowHover
                                    hideArrow
                                    trigger={
                                      <span className="tooltip-icon-wrapper">
                                        <MotifIcon
                                          iconFunction={IconoirInfoCircle}
                                          size="16"
                                          fill="#AFAEBA"
                                        />
                                      </span>
                                    }
                                  >
                                    <>
                                      <div className="info-title">
                                        Request in approval
                                      </div>
                                      <div className="info-content">
                                        This return request is currently under
                                        review. Once it’s resolved, you can
                                        submit a new request for the remaining
                                        quantity.
                                      </div>
                                    </>
                                  </MotifTooltip>
                                </div>
                              ) : (
                                "--"
                              )}
                            </td>
                            <td
                              className={
                                shouldShowReturnCost
                                  ? "return-cost-cell requested-amount text-right"
                                  : "return-cost-cell text-right"
                              }
                            >
                              {shouldShowReturnCost
                                ? formatCurrency(transaction.returnAmount)
                                : "--"}
                            </td>
                          </tr>
                        );
                      })}
                    {skuTransactionList.length === 0 && (
                      <tr>
                        <td colSpan={9} className="no-data">
                          You don’t have any returnable items available. A new
                          item becomes eligible after each billing cycle.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {skuTransactionList.length > 0 && (
                <div className="return-reason-section">
                  <p className="motif-body1-default-light rejection-reason">
                    Please enter your reason for returning the above SKUs
                    <span className="asterisk">*</span>
                  </p>
                  <MotifFormField role="group">
                    <MotifTextArea
                      aria-label="Return reason"
                      ariaLabel="Return reason"
                      className="rejection-textarea"
                      maxLength={RETURN_REASON_MAX_LENGTH}
                      onChange={handleChange}
                      placeholder="Enter a description..."
                      value={returnReason}
                      hideClearButton={true}
                    />
                    <div className="description-helper-text">
                      {returnReasonApiError && (
                        <span className="return-reason-error">
                          {returnReasonApiError}
                        </span>
                      )}
                      <span className="char-counter">
                        {returnReason.length}/{RETURN_REASON_MAX_LENGTH}
                      </span>
                    </div>
                  </MotifFormField>
                </div>
              )}
            </>
          )
        ) : (
          <div className="table-container previous-returns-table-container">
              <PreviousReturn renderHeaderWithTooltip={renderHeaderWithTooltip} renderUsagePeriodInTwoLines={renderUsagePeriodInTwoLines} getUnitsUsed={getUnitsUsed} orderSkuId={skuDetails?.OrderSkuId} />
          </div>
        )}
      </MotifModalBody>
      {activeTab === "request" && (
        <MotifModalFooter>
          <MotifButton
            type="button"
            variant="primary"
            className="cancel-btn"
            onClick={closeModal}
            size="medium"
            disabled={isSubmitApiLoading}
          >
            Cancel
          </MotifButton>
          <MotifButton
            variant="primary"
            size="medium"
            className="submit-btn"
            type="button"
            loading={isSubmitApiLoading}
            disabled={!isEnabled}
            onClick={submitHandler}
          >
            Submit
          </MotifButton>
        </MotifModalFooter>
      )}
    </MotifModal>
  );
}
